// SeoPagesCards.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/**
 * Props:
 *  - apiKey (string)   : required, sent as X-API-Key
 *  - baseURL (string)  : default https://rankridgeschools.com/rankridgeschool/dev/v1
 */
export default function SeoPagesCards({
  apiKey="12345678",
  baseURL = "https://gantasoft.in/rankridgecollege/dev/v1",
}) {
  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: { "X-API-Key": apiKey },
      }),
    [apiKey, baseURL]
  );

  // Data state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  // Filters / paging
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(9);

  // UI state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Default OG from site_settings
  const [defaultOG, setDefaultOG] = useState("");

  // File upload (modal)
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Load site settings once to determine default OG fallback
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/site_settings");
        const kv = {};
        (data.items || []).forEach((s) => {
          kv[s.skey] = s.svalue;
        });
        setDefaultOG(kv.default_og_image || kv.logo_path || "");
      } catch {
        // silently ignore; fallback stays empty
      }
    })();
  }, [api]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/seo_pages", {
        params: {
          search: q,
          page,
          limit,
          sort: "updated_at",
          order: "desc",
        },
      });
      if (data?.success) {
        setItems(data.items || []);
        setTotal(data.pagination?.total || 0);
      } else {
        setErr(data?.message || "Failed to load");
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Load error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(); // initial
  }, []); // eslint-disable-line

  useEffect(() => {
    load(); // on filter/paging change
  }, [q, page, limit]); // eslint-disable-line

  const pagesCount = Math.max(1, Math.ceil(total / limit));

  function openEdit(item) {
    setEditing({ ...item });
  }

  async function saveEdit() {
    if (!editing) return;
    const id = editing.id;
    if (!id) return;

    // Basic validation
    if (
      !editing.slug ||
      !editing.title ||
      !editing.description ||
      !editing.canonical_path
    ) {
      alert("Please fill slug, title, description, and canonical_path.");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/seo_pages/${id}`, {
        page_id: editing.page_id ? Number(editing.page_id) : null,
        subpage_id: editing.subpage_id ? Number(editing.subpage_id) : null,
        slug: editing.slug,
        title: editing.title,
        description: editing.description,
        canonical_path: editing.canonical_path,
        og_image: editing.og_image || null,
      });
      setEditing(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id) {
    if (!window.confirm("Delete this SEO page? This cannot be undone.")) return;
    try {
      await api.delete(`/seo_pages/${id}`);
      // Optimistic remove
      setItems((prev) => prev.filter((x) => x.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  }

  async function uploadOg() {
    const f = fileRef.current?.files?.[0];
    if (!f) return alert("Choose a file first.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      if (data?.success && data?.path) {
        setEditing((s) => ({ ...s, og_image: data.path }));
      } else {
        alert(data?.message || "Upload failed");
      }
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Upload error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = ""; // reset input
    }
  }

  return (
    <div style={wrap}>
      <h2 style={{ margin: 0 }}>SEO Pages</h2>

      <div style={toolbar}>
        <input
          placeholder="Search by slug/title/description"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
          style={input}
        />
        <select
          value={limit}
          onChange={(e) => {
            setPage(1);
            setLimit(Number(e.target.value));
          }}
          style={input}
        >
          {[6, 9, 12, 24, 48].map((n) => (
            <option key={n} value={n}>
              {n}/page
            </option>
          ))}
        </select>
        <button onClick={load} style={btn("#1f2937", "#fff")}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : err ? (
        <p style={{ color: "#dc2626" }}>{err}</p>
      ) : items.length === 0 ? (
        <div style={emptyBox}>No SEO pages found.</div>
      ) : (
        <>
          <div style={grid}>
            {items.map((it) => {
              const displayImg = it.og_image || defaultOG || "";
              return (
                <article key={it.id} style={card}>
                  <div style={{ display: "flex", gap: 12 }}>
                    <div
                      style={{
                        width: 96,
                        height: 96,
                        borderRadius: 10,
                        overflow: "hidden",
                        background: "#f3f4f6",
                        flexShrink: 0,
                        border: "1px solid #eef2f7",
                      }}
                    >
                      {displayImg ? (
                        <img
                          src={"https://gantasoft.in/rankridgeschool/dev/"+displayImg}
                          alt="og"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            padding: 8,
                            textAlign: "center",
                          }}
                        >
                          No image
                        </div>
                      )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <h3 style={{ margin: 0, fontSize: 16 }}>
                          {it.title || "(no title)"}
                        </h3>
                        {it.page_id ? (
                          <span style={chip}>page_id: {it.page_id}</span>
                        ) : null}
                        {it.subpage_id ? (
                          <span style={chip}>subpage_id: {it.subpage_id}</span>
                        ) : null}
                      </div>

                      <div style={{ marginTop: 6, color: "#374151", fontSize: 13 }}>
                        <div>
                          <strong>Slug:</strong>{" "}
                          <code style={code}>{it.slug}</code>
                        </div>
                        <div
                          style={{
                            marginTop: 4,
                            color: "#6b7280",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {it.description}
                        </div>
                        <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                          <strong>Canonical:</strong>{" "}
                          <code style={code}>{it.canonical_path}</code>
                        </div>
                      </div>

                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() => openEdit(it)}
                          style={btn("#2563eb", "#fff")}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDelete(it.id)}
                          style={btn("#ef4444", "#fff")}
                        >
                          Delete
                        </button>

                        {/* Quick “Use Default OG” if missing */}
                        {!it.og_image && defaultOG && (
                          <button
                            onClick={async () => {
                              try {
                                await api.patch(`/seo_pages/${it.id}`, {
                                  field: "og_image",
                                  value: defaultOG,
                                });
                                setItems((prev) =>
                                  prev.map((x) =>
                                    x.id === it.id ? { ...x, og_image: defaultOG } : x
                                  )
                                );
                              } catch (e) {
                                alert(
                                  e?.response?.data?.message ||
                                    e.message ||
                                    "Failed to set default OG"
                                );
                              }
                            }}
                            style={btn("#6b7280", "#fff")}
                          >
                            Use Default OG
                          </button>
                        )}

                        <span
                          style={{
                            marginLeft: "auto",
                            fontSize: 12,
                            color: "#6b7280",
                          }}
                        >
                          Updated:{" "}
                          {it.updated_at?.replace("T", " ").replace("Z", "") || "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div style={pager}>
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              style={btn("#e5e7eb", "#111")}
            >
              Prev
            </button>
            <span style={{ fontSize: 13, color: "#374151" }}>
              Page {page} / {pagesCount} (total {total})
            </span>
            <button
              disabled={page >= pagesCount}
              onClick={() => setPage((p) => p + 1)}
              style={btn("#e5e7eb", "#111")}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {editing && (
        <div style={modalBackdrop} onClick={() => setEditing(null)}>
          <div style={modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Edit SEO Page</h3>
            <div style={{ display: "grid", gap: 10 }}>
              <label style={lbl}>
                Page ID
                <input
                  type="number"
                  value={editing.page_id || ""}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, page_id: e.target.value }))
                  }
                  style={input}
                />
              </label>
              <label style={lbl}>
                Subpage ID
                <input
                  type="number"
                  value={editing.subpage_id || ""}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, subpage_id: e.target.value }))
                  }
                  style={input}
                />
              </label>
              <label style={lbl}>
                Slug
                <input
                  value={editing.slug || ""}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, slug: e.target.value }))
                  }
                  style={input}
                />
              </label>
              <label style={lbl}>
                Title
                <input
                  value={editing.title || ""}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, title: e.target.value }))
                  }
                  style={input}
                />
              </label>
              <label style={lbl}>
                Description
                <textarea
                  value={editing.description || ""}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, description: e.target.value }))
                  }
                  style={{ ...input, minHeight: 80 }}
                />
              </label>
              <label style={lbl}>
                Canonical Path
                <input
                  value={editing.canonical_path || ""}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, canonical_path: e.target.value }))
                  }
                  style={input}
                />
              </label>

              {/* OG Image controls */}
              <label style={lbl}>
                OG Image URL
                <input
                  value={editing.og_image || ""}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, og_image: e.target.value }))
                  }
                  style={input}
                />
              </label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input ref={fileRef} type="file" accept="image/*,.svg,.ico" />
                <button
                  onClick={uploadOg}
                  disabled={uploading}
                  style={btn("#059669", "#fff")}
                >
                  {uploading ? "Uploading…" : "Upload"}
                </button>
                {defaultOG && !editing.og_image && (
                  <button
                    onClick={() =>
                      setEditing((s) => ({ ...s, og_image: defaultOG }))
                    }
                    style={btn("#6b7280", "#fff")}
                  >
                    Use Default
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button
                onClick={saveEdit}
                disabled={saving}
                style={btn("#16a34a", "#fff")}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => setEditing(null)}
                style={btn("#e5e7eb", "#111")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* styles */
const wrap = { maxWidth: 1100, margin: "0 auto", padding: 16 };
const toolbar = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  margin: "12px 0 16px",
  flexWrap: "wrap",
};
const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: 12,
};
const card = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 14,
  background: "#fff",
};
const input = {
  padding: "9px 12px",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  minWidth: 160,
};
const code = { background: "#f3f4f6", padding: "2px 6px", borderRadius: 6 };
const chip = {
  fontSize: 11,
  background: "#eef2ff",
  color: "#3730a3",
  padding: "2px 6px",
  borderRadius: 999,
};
const btn = (bg, color) => ({
  padding: "8px 12px",
  background: bg,
  color,
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
});
const emptyBox = {
  border: "1px dashed #d1d5db",
  padding: 24,
  borderRadius: 12,
  color: "#6b7280",
  textAlign: "center",
};
const pager = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 16,
  justifyContent: "center",
};
const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 50,
};
const modal = {
  width: "min(680px, 100%)",
  background: "#fff",
  borderRadius: 14,
  maxHeight: "90vh",  
  overflowY: "auto",   
  padding: 16,
  boxShadow: "0 10px 30px rgba(0,0,0,.2)",
};
const lbl = { display: "grid", gap: 6, fontSize: 13, color: "#374151" };
